import _ from 'lodash';
import { sqInvestigateStore } from '@/core/core.stores';
import { COMMON_PROPS } from '@/trendData/baseItem.constants';
import { BaseItemStore } from '@/trendData/baseItem.store';
import { splitUniqueId } from '@/trendData/trendCapsule.utilities';
import { ITEM_TYPES, PREVIEW_ID } from '@/trendData/trendData.constants';
import type { UsedCapsulePropertyValues, UserCapsulePropertyColorSettings } from '@/trendData/trendData.constants';
import type { CapsuleItem, CapsuleToAdd, PreviewCapsulesDefinition } from '@/trendData/trendData.types';

export type SelectedCapsule = {
  id: string;
  startTime?: number;
  endTime?: number;
  isUncertain?: boolean;
  cursorKey?: number;
};

export type TrendCapsuleStoreDehydratedState = Omit<TrendCapsuleStoreState, 'items'>;

interface TrendCapsuleStoreState {
  /** Always empty: the capsule dataset lives in the capsule runtime store. Kept for BaseItemStore compatibility. */
  items: CapsuleItem[];
  selectedCapsules: SelectedCapsule[];
  trendCapsulePropertyColors: UserCapsulePropertyColorSettings;
  usedCapsulePropertyValues: UsedCapsulePropertyValues;
  editingId?: string | null;
  previewCapsulesDefinition?: PreviewCapsulesDefinition;
}

/**
 * The persisted half of the capsule state: which capsules the user has selected, which condition is
 * being edited, how previews regenerate on scroll, and the user-picked capsule property colors. The
 * capsule dataset itself (items, chart items, stitch details, preview rows) is runtime-only and
 * lives in `capsuleRuntime.store.ts`; the merge happens in `trendCapsule.selectors.ts`.
 */
export class TrendCapsuleStore extends BaseItemStore<CapsuleItem> {
  static readonly storeName = 'sqTrendCapsuleStore';

  /**
   * Color is dependent on Condition store.
   */
  rehydrateWaitFor = ['sqTrendConditionStore'];

  initialize(): void {
    const initialState: TrendCapsuleStoreState = {
      ...COMMON_PROPS,
      items: [],
      selectedCapsules: [],
      trendCapsulePropertyColors: {},
      usedCapsulePropertyValues: [],
    };
    this.state = this.immutable(initialState);
  }

  /**
   * Returns an array containing the capsule items that are selected
   */
  get selectedCapsules(): SelectedCapsule[] {
    return this.state.get('selectedCapsules') ?? [];
  }

  /**
   * Returns the Id of the condition that is being edited.
   */
  get editingId(): string | undefined {
    return this.state.get('editingId');
  }

  /**
   * Returns an Object describing a condition (so that it can be updated upon scrolling)
   */
  get previewCapsuleDefinition(): PreviewCapsulesDefinition | undefined {
    return this.state.get('previewCapsulesDefinition');
  }

  get usedCapsulePropertyValues(): UsedCapsulePropertyValues {
    return this.state.get('usedCapsulePropertyValues');
  }

  /**
   * The colors the user has to use for coloring conditions in non-capsule time trend views.
   */
  get trendCapsulePropertyColors(): UserCapsulePropertyColorSettings {
    return this.state.get('trendCapsulePropertyColors');
  }

  /**
   * Determines if a capsule is selected.
   *
   * @param {String} id - The id of the capsule
   *
   * @returns {Boolean} True if it is selected, false otherwise
   */
  isSelected(id: string): boolean {
    return _.some(this.state.get('selectedCapsules'), ['id', id]);
  }

  protected readonly handlers = {
    ...super.baseHandlers,
    TREND_ADD_CAPSULES: this.addCapsules,
    TREND_SET_EDITING_CAPSULE_SET_ID: this.setEditingId,
    TREND_SET_CHART_CAPSULES_PREVIEW: this.setPreviewChartItem,
    TREND_REMOVE_CHART_CAPSULES_PREVIEW: this.removePreviewChartItem,
    TREND_REMOVE_CAPSULE_SET_FROM_PREVIEW_LANE: this.removeConditionFromPreviewLane,
    TREND_REMOVE_ITEMS: this.removeCapsules,
    TREND_SET_SELECTED_CAPSULES: this.setSelectedCapsules,
    TREND_SET_SELECTED: this.setCapsuleSelected,
    TREND_UNSELECT_ALL_CAPSULES: this.unselectAllCapsules,
    TREND_REPLACE_CAPSULE_SELECTION: this.replaceCapsuleSelection,
    SET_TREND_CAPSULE_PROPERTY_COLORS: ({
      trendCapsulePropertyColors,
    }: {
      trendCapsulePropertyColors: UserCapsulePropertyColorSettings;
    }) => {
      this.state.set('trendCapsulePropertyColors', trendCapsulePropertyColors);
    },
    SET_USED_CAPSULE_PROPERTY_VALUES: ({ usedCapsulePropertyValues }: { usedCapsulePropertyValues: string[] }) => {
      this.state.set('usedCapsulePropertyValues', usedCapsulePropertyValues);
    },
  };

  /**
   * Keeps the selection stable across a capsule fetch: when an incoming capsule replaces an
   * uncertain one under a new id, the selection entry moves to the replacement. The capsule items
   * themselves are recorded in the capsule runtime store by the dispatching action.
   */
  private addCapsules({
    uncertainReplacements = {},
  }: {
    capsules: CapsuleToAdd[];
    numPixels: number;
    /** Uncertain-capsule replacements the dispatching action derived, shared with the series store */
    uncertainReplacements?: Partial<Record<string, CapsuleToAdd>>;
  }) {
    const kept: SelectedCapsule[] = [];
    const replacements: SelectedCapsule[] = [];
    (this.state.get('selectedCapsules') as SelectedCapsule[]).forEach((selectedCapsule) => {
      const replacement = uncertainReplacements[selectedCapsule.id];
      if (replacement === undefined) {
        kept.push(selectedCapsule);
      } else {
        const { id, startTime, endTime, isUncertain } = replacement;
        replacements.push({ id, startTime: startTime ?? undefined, endTime: endTime ?? undefined, isUncertain });
      }
    });

    this.state.set('selectedCapsules', kept.concat(replacements));
  }

  /**
   * Sets the id of the Condition being edited. If any capsules were selected, they are removed.
   *
   * @param {Object} payload - Object container for arguments
   * @param {String} payload.id - the id.
   */
  private setEditingId(payload: { id: string }) {
    this.state.set('editingId', _.get(payload, 'id', PREVIEW_ID));
    this.state.set('previewCapsulesDefinition', {});
    this.removeCapsulesByConditionId(payload.id);
  }

  /**
   * Utility function to remove all selected capsules that belong to the provided condition
   *
   * @param {String} conditionId - GUID of the condition whose capsules should be remove
   */
  private removeCapsulesByConditionId(conditionId: string) {
    const selectedCapsules = _.clone(this.state.get('selectedCapsules'));
    const removedSelection = _.remove(
      selectedCapsules,
      (selectedCapsule: SelectedCapsule) => conditionId === splitUniqueId(selectedCapsule.id).conditionId,
    );
    if (removedSelection.length) {
      this.state.set('selectedCapsules', selectedCapsules);
    }
  }

  /**
   * Stores the Object that defines how to generate the preview capsules so they can be updated when
   * the user scrolls, and prunes selections that no longer exist among the previewed capsules. The
   * preview rows themselves are recorded in the capsule runtime store by the dispatching action.
   */
  private setPreviewChartItem(payload: {
    formula: string;
    parameters: unknown[];
    color: string;
    id?: string;
    capsules?: unknown[];
  }) {
    const id = payload.id || PREVIEW_ID;

    this.state.set('previewCapsulesDefinition', {
      formula: payload.formula,
      parameters: payload.parameters,
      color: payload.color,
      id,
    });

    // Preview capsules inherently change a lot so we clear any selected capsules that don't seem to exist any more
    const cursor = this.state.select('selectedCapsules');
    const capsules: { id: string }[] = _.get(payload, ['capsules', 'capsules'], []);
    const selections = cursor.get() as SelectedCapsule[];
    for (let index = selections.length - 1; index >= 0; index -= 1) {
      const split = splitUniqueId(selections[index].id);
      if (split.conditionId === id && !capsules.some((capsule) => split.capsuleId === capsule.id)) {
        cursor.unset(index);
      }
    }
  }

  /**
   * Clears the editing and preview-definition state; the preview rows are cleared from the capsule
   * runtime store by the dispatching action.
   */
  private removePreviewChartItem() {
    if (this.state.get(['previewCapsulesDefinition', 'id'])) {
      this.removeCapsulesByConditionId(this.state.get(['previewCapsulesDefinition', 'id']));
    }

    this.state.merge({
      editingId: null,
      previewCapsulesDefinition: {},
    });
  }

  /**
   * Ends the edit of a condition whose lane is removed from preview; the preview rows are updated in
   * the capsule runtime store by the dispatching action.
   */
  private removeConditionFromPreviewLane() {
    this.state.merge({ editingId: null });
  }

  /**
   * Augments the sqBaseItemStore functionality of removing items by also handling the removal of selectedCapsules if
   * their parent condition is also removed.
   *
   * @param {Object} payload - Object container for arguments
   * @param {Object[]} payload.items - An array of items to remove
   */
  private removeCapsules(payload: { items: Array<{ itemType: string; id: string }>; numPixels?: number }) {
    this.removeItems(payload);
    // Don't remove child selections in capsule picking mode to avoid the loss of selected capsules
    if (!sqInvestigateStore.isCapsulePickingMode) {
      _.chain(payload.items)
        .filter(['itemType', ITEM_TYPES.CONDITION])
        .forEach((condition) => this.removeCapsulesByConditionId(condition.id))
        .value();
    }
  }

  /**
   * Updates selection state for many capsules in a single pass.
   *
   * @param payload.capsules - Capsules to select or unselect
   * @param payload.selected - Selection status
   * @returns true if selection state was updated
   */
  private applyCapsulesSelection({ capsules, selected }: { capsules: SelectedCapsule[]; selected: boolean }): boolean {
    if (capsules.length === 0) {
      return false;
    }

    const capsuleIds = new Set(capsules.map((capsule) => capsule.id));
    const selectedCapsules = this.state.get('selectedCapsules') as SelectedCapsule[];

    if (selected) {
      const seenIds = new Set(selectedCapsules.map((capsule) => capsule.id));
      const toAdd = capsules
        .filter((capsule) => {
          if (seenIds.has(capsule.id)) {
            return false;
          }
          seenIds.add(capsule.id);
          return true;
        })
        .map(
          (capsule): SelectedCapsule => ({
            id: capsule.id,
            startTime: capsule.startTime ?? undefined,
            endTime: capsule.endTime ?? undefined,
            isUncertain: capsule.isUncertain,
          }),
        );
      if (toAdd.length > 0) {
        this.state.set('selectedCapsules', selectedCapsules.concat(toAdd));
      }
    } else {
      this.state.set(
        'selectedCapsules',
        selectedCapsules.filter((capsule) => !capsuleIds.has(capsule.id)),
      );
    }

    return true;
  }

  /**
   * Selects or unselects multiple capsules.
   *
   * @param payload - Object container for arguments
   * @param payload.capsules - An array of capsules that will be selected or unselected
   * @param payload.resolvedCapsules - The same capsules resolved against the capsule dataset by the
   *   dispatching action; tracked in place of `capsules` when present
   * @param payload.selected - Selection status
   */
  private setSelectedCapsules({
    capsules,
    resolvedCapsules,
    selected,
  }: {
    capsules: SelectedCapsule[];
    resolvedCapsules?: SelectedCapsule[];
    selected: boolean;
  }) {
    this.applyCapsulesSelection({ capsules: resolvedCapsules ?? capsules, selected });
  }

  /**
   * Tracks capsule selection in selectedCapsules.
   *
   * @param {Object} payload - Object container for arguments
   * @param {Object} payload.item - The item to select
   * @param {Object} [payload.resolvedItem] - The item resolved against the capsule dataset by the
   *   dispatching action; tracked in place of `item` when present
   * @param {Boolean} payload.selected - Selection status
   */
  private setCapsuleSelected(payload: {
    item: { id: string; itemType?: string } & SelectedCapsule;
    resolvedItem?: SelectedCapsule;
    selected: boolean;
  }) {
    if (payload.item.itemType !== ITEM_TYPES.CAPSULE) {
      return;
    }

    const { id, startTime, endTime, isUncertain } = payload.resolvedItem ?? payload.item;
    const index = (this.state.get('selectedCapsules') as SelectedCapsule[]).findIndex((capsule) => capsule.id === id);
    if (payload.selected && index < 0) {
      this.state.push('selectedCapsules', { id, startTime, endTime, isUncertain });
    } else if (!payload.selected && index >= 0) {
      this.state.splice('selectedCapsules', [index, 1]);
    }
  }

  /**
   * Unselects all capsules.
   */
  private unselectAllCapsules() {
    this.state.set('selectedCapsules', []);
  }

  /**
   * Overwrite the `selectedCapsules` with the provided capsules.
   * Used when Manual Condition closes and the pre-tool capsule selection is restored.
   *
   * @param {Object} payload - Object container for arguments
   * @param {Object[]} payload.capsules - Selected capsules
   * @param {String} payload.capsules[].id - capsule's unique id created from `getUniqueId`
   * @param {Number} payload.capsules[].startTime - end of the capsule in ms
   * @param {Number} payload.capsules[].endTime - start of capsule in ms
   * @param {Boolean} payload.capsules[].isUncertain - indicates that the capsule is uncertain
   * @param {Number} payload.capsules[].cursorKey - start of uncertainty
   */
  private replaceCapsuleSelection({
    capsules,
  }: {
    capsules: Array<{ id: string; startTime?: number; endTime?: number; isUncertain?: boolean; cursorKey?: number }>;
  }) {
    this.state.set(
      'selectedCapsules',
      _.map(capsules, (c) => _.pick(c, ['id', 'startTime', 'endTime', 'isUncertain', 'cursorKey'])),
    );
  }

  /**
   * Exports state so it can be used to re-create the state later using `rehydrate`.
   *
   * @returns {Object} The dehydrated items.
   */
  dehydrate(): TrendCapsuleStoreDehydratedState {
    const { items, ...persisted } = this.state.serialize();
    return persisted;
  }

  /**
   * Re-creates the dehydrated capsules.
   *
   * @param {Object} dehydratedState Previous state usually obtained from `dehydrate` method.
   */
  rehydrate(dehydratedState: Partial<TrendCapsuleStoreDehydratedState>): void {
    this.state.merge(dehydratedState);
  }
}
